import { Box, Button, Chip, DialogActions, DialogContent, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useGetAdminDeviationDetail } from 'src/api/deviation'
import FilePreviewGrid from 'src/components/common/content/FilePreviewGrid'
import { IDeviationKeywords } from 'src/generated-types/deviation-keywords'
import { IFile } from 'src/generated-types/file'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { ShowDialogProps } from 'src/show-dialog'
import { FollowButton } from 'src/components/notifications/FollowButton'
import { isFileImage, getFormattedDate } from 'src/utils'

interface DeviationDetailDialogProps extends ShowDialogProps {
  customerId: EntityId
  deviationId: number
}

const getContentTypeFromUrl = (url: string): string => {
  const extension = url.split('?')[0].split('.').pop()?.toLowerCase()
  const typeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }
  return typeMap[extension || ''] || ''
}

const DeviationDetailDialog = ({ pop, customerId, deviationId }: DeviationDetailDialogProps) => {
  const { t: translate } = useTranslation(['common', 'deviation'])
  const { openImagePreviewDialog, openFilePreviewDialog } = useDialogCtx()

  const { data: deviation, isLoading } = useGetAdminDeviationDetail(customerId, deviationId, {
    enabled: !!customerId && !!deviationId,
  })

  const renderField = (label: string, value: any) => {
    if (value === null || value === undefined || value === '') return null

    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2">{value}</Typography>
      </Box>
    )
  }

  const renderConnection = (label: string, connection: { id: number; name: string } | undefined) => {
    if (!connection) return null

    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2">{connection.name}</Typography>
      </Box>
    )
  }

  const renderKeywords = (keywordsString: string | undefined) => {
    if (!keywordsString) return null

    try {
      const keywordArray = JSON.parse(keywordsString)
      if (!Array.isArray(keywordArray) || keywordArray.length === 0) return null

      const keywordLabels = keywordArray.map((idx: number) => translate(IDeviationKeywords[idx], { ns: 'deviation' }))

      return (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {translate('keywords', { ns: 'common' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {keywordLabels.map((label, idx) => (
              <Chip key={idx} label={label} size="small" />
            ))}
          </Box>
        </Box>
      )
    } catch {
      return null
    }
  }

  const fileItems =
    deviation?.files?.map(
      file =>
        ({
          id: file.id,
          filename: file.name,
          url: file.url,
          viewToken: '',
          contentType: getContentTypeFromUrl(file.url),
          contentLength: 0,
          createdAt: new Date(),
          createdByUserId: '',
          createdByUser: undefined,
          updatedAt: new Date(),
          updatedByUserId: '',
          updatedByUser: undefined,
          deletedAt: undefined,
          deletedByUserId: '',
          deletedByUser: undefined,
        } as unknown as IFile),
    ) ?? []

  const handleFileClick = (file: IFile) => {
    if (isFileImage(file)) {
      openImagePreviewDialog(file.url, file.filename, file.filename)
    } else {
      openFilePreviewDialog(file.url, file.filename)
    }
  }

  return (
    <>
      <DialogContent className="scrollbar">
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : deviation ? (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 2, minWidth: 260 }}>
                <Typography variant="h5">
                  {deviation.title || translate('deviation-details', { ns: 'deviation' })}
                </Typography>
                {deviation.description && <Typography variant="caption">{deviation.description}</Typography>}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 1,
                }}
              >
                <FollowButton customerId={customerId} entityType="Deviation" entityId={deviationId} />
                {deviation.createdAt && (
                  <Typography variant="caption">
                    {translate('createdAt', { ns: 'common' })}:{' '}
                    {getFormattedDate(new Date(deviation.createdAt + 'Z'), 'YYYY-MM-DD HH:mm')}
                  </Typography>
                )}
                {deviation.createdByUserName && (
                  <Typography variant="caption">
                    {translate('created-by', { ns: 'common' })}: {deviation.createdByUserName}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Basic Information and Connections */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('basic-info', { ns: 'deviation' })}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  {renderField(translate('type', { ns: 'common' }), deviation.type)}

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {translate('status', { ns: 'common' })}
                    </Typography>
                    <Chip
                      label={
                        deviation.isOpen ? translate('open', { ns: 'common' }) : translate('closed', { ns: 'common' })
                      }
                      color={deviation.isOpen ? 'error' : 'success'}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {translate('priority', { ns: 'common' })}
                    </Typography>
                    <Chip
                      label={deviation.priority}
                      color={deviation.priority === 'Critical' ? 'error' : 'default'}
                      size="small"
                    />
                  </Box>

                  {renderField(translate('locality', { ns: 'common' }), deviation.localityName)}
                  {renderField(translate('locality-group', { ns: 'common' }), deviation.localityGroupName)}
                </Box>
              </Box>

              {/* Connections */}
              {(deviation.mooring ||
                deviation.position ||
                deviation.mainComponent ||
                deviation.component ||
                deviation.scheduledTask ||
                deviation.maintenance) && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    {translate('connections', { ns: 'deviation' })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                    }}
                  >
                    {renderConnection(translate('mooring', { ns: 'common' }), deviation.mooring)}
                    {renderConnection(translate('position', { ns: 'common' }), deviation.position)}
                    {renderConnection(translate('main-component', { ns: 'common' }), deviation.mainComponent)}
                    {renderConnection(translate('component', { ns: 'common' }), deviation.component)}
                    {renderConnection(translate('task', { ns: 'common' }), deviation.scheduledTask)}
                    {renderConnection(translate('maintenance', { ns: 'common' }), deviation.maintenance)}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Additional Details */}
            {(deviation.reason || deviation.measures || deviation.keywords) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('additional-details', { ns: 'deviation' })}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>{renderField(translate('reason', { ns: 'deviation' }), deviation.reason)}</Box>
                  <Box>{renderField(translate('measures', { ns: 'deviation' }), deviation.measures)}</Box>
                  {renderKeywords(deviation.keywords)}
                </Box>
              </>
            )}

            {/* Classifications */}
            {(deviation.visualDeviation ||
              deviation.measuredDeviation ||
              deviation.normalWear ||
              deviation.abnormalWear) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('classifications', { ns: 'deviation' })}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {deviation.visualDeviation && (
                    <Chip label={translate('visual-deviation', { ns: 'deviation' })} size="small" />
                  )}
                  {deviation.measuredDeviation && (
                    <Chip label={translate('measured-deviation', { ns: 'deviation' })} size="small" />
                  )}
                  {deviation.normalWear && <Chip label={translate('normal-wear', { ns: 'deviation' })} size="small" />}
                  {deviation.abnormalWear && (
                    <Chip label={translate('abnormal-wear', { ns: 'deviation' })} size="small" />
                  )}
                </Box>
              </>
            )}

            {/* Follow-ups */}
            {deviation.followUps && deviation.followUps.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('follow-ups', { ns: 'deviation' })} ({deviation.followUps.length})
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {deviation.followUps.map(followUp => (
                    <Box key={followUp.id} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          label={
                            followUp.status
                              ? translate('closed', { ns: 'common' })
                              : translate('open', { ns: 'common' })
                          }
                          color={followUp.status ? 'success' : 'error'}
                          size="small"
                        />
                        <Chip
                          label={followUp.priority}
                          color={followUp.priority === 'Critical' ? 'error' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {followUp.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('performed-by', { ns: 'deviation' })}: {followUp.performedBy} |{' '}
                        {getFormattedDate(new Date(followUp.createdAt + 'Z'), 'YYYY-MM-DD HH:mm')}
                        {followUp.dueDate &&
                          ` | ${translate('due-date', { ns: 'deviation' })}: ${getFormattedDate(
                            new Date(followUp.dueDate + 'Z'),
                            'YYYY-MM-DD',
                          )}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Files */}
            {fileItems && fileItems.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('files', { ns: 'deviation' })} ({fileItems.length})
                </Typography>
                <FilePreviewGrid files={fileItems} onFileClick={handleFileClick} />
              </>
            )}
          </Box>
        ) : (
          <Typography>{translate('no-data', { ns: 'common' })}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => pop()}>{translate('close', { ns: 'common', defaultValue: 'Close' })}</Button>
      </DialogActions>
    </>
  )
}

export default DeviationDetailDialog
